/**
 * P_WEEKLY_REPORT_CARD_2.3 — Settings tab.
 *
 * Per-org delivery preferences. Tuesday send time + timezone are
 * persisted but not yet wired to a cron — the automated dispatcher
 * is a Phase 2.4 deliverable. Today the operator drives every send
 * via the Distribution Center, so these fields capture intent.
 *
 * Authorization is enforced server-side: client_admin can only
 * read/write their own org's settings.
 */
import { useEffect, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';

import { weeklyDistributionAPI } from '../../lib/api';

const COMMON_TIMEZONES = [
    'America/Toronto', 'America/New_York', 'America/Chicago', 'America/Denver',
    'America/Los_Angeles', 'America/Vancouver', 'America/Halifax',
    'Europe/Paris', 'Europe/London', 'UTC',
];

const PUBLICATION_DAYS = [
    'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday',
];

export default function WeeklyReportSettings({ organizationId, t }) {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [dirty, setDirty] = useState(false);

    useEffect(() => {
        if (!organizationId) return;
        let cancelled = false;
        setLoading(true);
        (async () => {
            try {
                const res = await weeklyDistributionAPI.getSettings(organizationId);
                if (!cancelled) {
                    setData(res.data);
                    setDirty(false);
                }
            } catch {
                if (!cancelled) toast.error(t('weeklyReport.states.error'));
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => { cancelled = true; };
    }, [organizationId, t]);

    const patch = (k, v) => {
        setData((prev) => ({ ...(prev || {}), [k]: v }));
        setDirty(true);
    };

    const handleSave = async () => {
        if (!data) return;
        setSaving(true);
        try {
            const payload = {
                publication_day: data.publication_day,
                publication_time: data.publication_time,
                timezone: data.timezone,
                enabled: data.enabled,
                email_enabled: data.email_enabled,
                subject_template: data.subject_template ?? null,
                sender_name: data.sender_name ?? null,
                reply_to: data.reply_to ?? null,
            };
            const res = await weeklyDistributionAPI.updateSettings(organizationId, payload);
            setData(res.data);
            setDirty(false);
            toast.success(t('weeklyReport.settings.saved'));
        } catch (e) {
            toast.error(e?.response?.data?.detail || t('weeklyReport.states.error'));
        } finally {
            setSaving(false);
        }
    };

    if (loading || !data) {
        return (
            <div className="py-32 flex flex-col items-center gap-6">
                <Loader2 className="w-8 h-8 animate-spin opacity-50" />
            </div>
        );
    }

    return (
        <section data-testid="weekly-report-settings">
            <h2 className="text-xs sm:text-sm uppercase tracking-[0.32em] opacity-60 mb-16 sm:mb-20">
                {t('weeklyReport.settings.title')}
            </h2>

            <div className="space-y-12 max-w-xl">
                <ToggleRow
                    label={t('weeklyReport.settings.enabled')}
                    value={!!data.enabled}
                    onChange={(v) => patch('enabled', v)}
                    testId="weekly-report-settings-enabled"
                />
                <ToggleRow
                    label={t('weeklyReport.settings.emailEnabled')}
                    value={!!data.email_enabled}
                    onChange={(v) => patch('email_enabled', v)}
                    testId="weekly-report-settings-email-enabled"
                />
                <SelectField
                    label={t('weeklyReport.settings.publicationDay')}
                    value={data.publication_day || 'tuesday'}
                    options={PUBLICATION_DAYS}
                    optionLabel={(v) => t(`weeklyReport.days.${v}`)}
                    onChange={(v) => patch('publication_day', v)}
                    testId="weekly-report-settings-publication-day"
                />
                <TextField
                    label={t('weeklyReport.settings.publicationTime')}
                    placeholder="HH:MM"
                    value={data.publication_time || ''}
                    onChange={(v) => patch('publication_time', v)}
                    testId="weekly-report-settings-publication-time"
                />
                <SelectField
                    label={t('weeklyReport.settings.timezone')}
                    value={data.timezone || 'America/Toronto'}
                    options={COMMON_TIMEZONES}
                    onChange={(v) => patch('timezone', v)}
                    testId="weekly-report-settings-timezone"
                />
                <TextField
                    label={t('weeklyReport.settings.senderName')}
                    placeholder={t('weeklyReport.settings.senderNamePlaceholder')}
                    value={data.sender_name || ''}
                    onChange={(v) => patch('sender_name', v)}
                    testId="weekly-report-settings-sender-name"
                />
                <TextField
                    label={t('weeklyReport.settings.replyTo')}
                    placeholder="reply@yourdomain.com"
                    value={data.reply_to || ''}
                    onChange={(v) => patch('reply_to', v)}
                    testId="weekly-report-settings-reply-to"
                />
                <TextField
                    label={t('weeklyReport.settings.subjectTemplate')}
                    placeholder={t('weeklyReport.settings.subjectTemplatePlaceholder')}
                    value={data.subject_template || ''}
                    onChange={(v) => patch('subject_template', v)}
                    testId="weekly-report-settings-subject"
                />
            </div>

            <div className="mt-16 flex items-center gap-4">
                <button
                    type="button"
                    onClick={handleSave}
                    disabled={!dirty || saving}
                    data-testid="weekly-report-settings-save-btn"
                    className="px-5 py-2.5 text-xs uppercase tracking-[0.2em] border border-white/30 rounded-full hover:border-white/80 transition-colors disabled:opacity-40"
                >
                    {saving ? t('weeklyReport.settings.saving') : t('weeklyReport.settings.save')}
                </button>
                {dirty && (
                    <span className="text-xs opacity-50">{t('weeklyReport.settings.unsaved')}</span>
                )}
            </div>
        </section>
    );
}

const ToggleRow = ({ label, value, onChange, testId }) => (
    <div className="flex items-center justify-between gap-6">
        <span className="text-base sm:text-lg font-light">{label}</span>
        <button
            type="button"
            role="switch"
            aria-checked={value}
            onClick={() => onChange(!value)}
            data-testid={testId}
            className={`relative inline-flex h-7 w-12 items-center rounded-full transition-colors ${
                value ? 'bg-emerald-300/70' : 'bg-white/15'
            }`}
        >
            <span
                className={`inline-block h-5 w-5 transform rounded-full bg-white transition-transform ${
                    value ? 'translate-x-6' : 'translate-x-1'
                }`}
            />
        </button>
    </div>
);

const TextField = ({ label, value, onChange, placeholder, testId }) => (
    <label className="block">
        <span className="block text-xs uppercase tracking-[0.24em] opacity-60 mb-3">{label}</span>
        <input
            type="text"
            value={value}
            placeholder={placeholder}
            onChange={(e) => onChange(e.target.value)}
            data-testid={testId}
            className="w-full bg-transparent border-b border-white/20 px-1 py-3 text-base focus:outline-none focus:border-white/60"
        />
    </label>
);

const SelectField = ({ label, value, options, optionLabel, onChange, testId }) => (
    <label className="block">
        <span className="block text-xs uppercase tracking-[0.24em] opacity-60 mb-3">{label}</span>
        <select
            value={value}
            onChange={(e) => onChange(e.target.value)}
            data-testid={testId}
            className="w-full bg-transparent border-b border-white/20 px-1 py-3 text-base focus:outline-none focus:border-white/60"
        >
            {options.map((opt) => (
                <option key={opt} value={opt} className="bg-slate-900">
                    {optionLabel ? optionLabel(opt) : opt}
                </option>
            ))}
        </select>
    </label>
);
